import React, { useEffect, useState, useRef } from 'react';
import type { AnalysisCategory } from '../types';
import useClickOutside from '../hooks/useClickOutside';

interface UploadAreaProps {
  selectedCategory: string;
  availableCategories: { [key: string]: AnalysisCategory };
  onSelectCategory: (category: string) => void;
  loading: boolean;
  loadingMessage?: string;
  error?: string | null;
  onSubmit: (args: { selectedCategory: string; inputText: string; selectedFiles: File[]; formData: Record<string, string | boolean> }) => void;
  displayName?: string;
  userName?: string;
}

const UploadArea: React.FC<UploadAreaProps> = ({
  selectedCategory,
  availableCategories,
  onSelectCategory,
  loading,
  loadingMessage,
  error,
  onSubmit,
  displayName,
  userName,
}) => {
  const [dragOver, setDragOver] = useState<boolean>(false);
  const [showUploadMenu, setShowUploadMenu] = useState<boolean>(false);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [modalSrc, setModalSrc] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [localSelectedFiles, setLocalSelectedFiles] = useState<File[]>([]);
  const [localDropError, setLocalDropError] = useState<string | null>(null);
  const [fileObjectUrls, setFileObjectUrls] = useState<(string | null)[]>([]);
  const [localInputText, setLocalInputText] = useState<string>('');
  const [localFormData, setLocalFormData] = useState<Record<string, string | boolean>>({});

  const getCategoryConfig = () => availableCategories?.[selectedCategory];

  const uploadMenuRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(uploadMenuRef, () => setShowUploadMenu(false), showUploadMenu);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      const allowed = files.filter(f => isFileAllowed(f));
      const rejected = files.filter(f => !isFileAllowed(f));
      if (allowed.length) setLocalSelectedFiles(prev => [...prev, ...allowed]);
      if (rejected.length) {
        const names = rejected.map(r => r.name).join(', ');
        setLocalDropError(`Ignored unsupported file(s): ${names}`);
        window.setTimeout(() => setLocalDropError(null), 5000);
      }
      setShowUploadMenu(false);
      event.currentTarget.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer.files) {
      const files = Array.from(event.dataTransfer.files);
      const allowed = files.filter(f => isFileAllowed(f));
      const rejected = files.filter(f => !isFileAllowed(f));
      if (allowed.length) setLocalSelectedFiles(prev => [...prev, ...allowed]);
      if (rejected.length) {
        const names = rejected.map(r => r.name).join(', ');
        setLocalDropError(`Ignored unsupported file(s): ${names}`);
        window.setTimeout(() => setLocalDropError(null), 5000);
      }
    }
  };

  const isFileAllowed = (file: File) => {
    const cfg = getCategoryConfig();
    const allowed = cfg?.file_types;
    if (!allowed || !allowed.length) return true;

    const name = file.name || '';
    const type = file.type || '';

    for (const pattern of allowed) {
      if (!pattern) continue;
      const p = pattern.toLowerCase();
      if (p.startsWith('.')) {
        if (name.toLowerCase().endsWith(p)) return true;
        continue;
      }
      if (!p.includes('/')) {
        if (name.toLowerCase().endsWith('.' + p)) return true;
        continue;
      }
      if (p.endsWith('/*')) {
        const prefix = p.split('/')[0];
        if (type.toLowerCase().startsWith(prefix + '/')) return true;
      } else {
        if (type.toLowerCase() === p) return true;
      }
    }
    return false;
  };

  const openImageModal = (index: number) => {
    setModalIndex(index);
  };

  const closeImageModal = () => {
    setModalIndex(null);
  };

  // Create/revoke object URL for modal image and handle Escape key + body scroll lock
  useEffect(() => {
    let url: string | null = null;
    if (modalIndex !== null) {
      const file = localSelectedFiles[modalIndex];
      if (file) {
        url = URL.createObjectURL(file);
        setModalSrc(url);
      }
      // lock body scroll
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closeImageModal();
      };
      window.addEventListener('keydown', onKey);

      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prevOverflow;
        if (url) URL.revokeObjectURL(url);
        setModalSrc(null);
      };
    }
    // If modalIndex is null, ensure modalSrc cleared
    setModalSrc(null);
    return;
  }, [modalIndex, localSelectedFiles]);

  useEffect(() => {
    const urls = localSelectedFiles.map((f) => URL.createObjectURL(f));
    setFileObjectUrls(urls);

    return () => {
      urls.forEach((u) => {
        if (u) URL.revokeObjectURL(u);
      });
    };
  }, [localSelectedFiles]);

  useClickOutside(modalRef, () => {
    if (modalIndex !== null) closeImageModal();
  }, modalIndex !== null);

  const getPlaceholderText = () => {
    if (!selectedCategory) return "How can I help you today?";
    
    switch (selectedCategory) {
      case 'medical_image_analysis':
        return "Upload and describe the medical images or documents for diagnostic analysis...";
      case 'audio_transcription':
        return "Upload audio files to extract, transcribe, and index all spoken content...";
      case 'scene_ai_analysis':
        return "Upload and describe property images or describe the scene for detailed analysis...";
      case 'video_transcription':
        return "Upload video files to extract, transcribe, and index all spoken content...";
      default:
        return "Describe what you'd like to analyze...";
    }
  };

  const renderFormFields = () => {
    const categoryConfig = getCategoryConfig();
    if (!categoryConfig?.required_fields?.length) return null;

    return (
      <div className="space-y-4 mb-4">
        {categoryConfig.required_fields.map(field => {
          const fieldDef = categoryConfig.field_definitions?.[field];
          const fieldLabel = fieldDef?.label || field.replace(/_/g, ' ');
          const placeholder = fieldDef?.placeholder || `Enter ${fieldLabel.toLowerCase()}`;

          return (
            <div key={field}>
              <div className={`border border-border-secondary rounded-xl bg-bg-secondary focus-within:border-border-focus transition-all ${loading ? 'opacity-50' : ''}`}>
                <input
                  type="text"
                  id={field}
                  value={String(typeof localFormData[field] === 'string' ? localFormData[field] : '')}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, [field]: e.target.value }))}
                  placeholder={placeholder}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-transparent border-none rounded-xl text-text-primary placeholder-text-muted focus:outline-none"
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getCategoryIcon = (categoryKey: string) => {
    switch (categoryKey) {
      case 'medical_image_analysis':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'audio_transcription':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        );
      case 'scene_ai_analysis':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'video_transcription':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const getCategoryButtonText = (categoryKey: string) => {
    switch (categoryKey) {
      case 'medical_image_analysis':
        return 'Medical';
      case 'audio_transcription':
        return 'Audio';
      case 'scene_ai_analysis':
        return 'Scene';
      case 'video_transcription':
        return 'Video';
      default:
        return 'Analysis';
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center max-w-2xl mx-auto px-6 w-full">
      {/* Company Logo Banner */}
      <div className="mb-6 w-full flex justify-center">
        <img
          src="/morgan-morgan-logo.png"
          alt="Morgan and Morgan"
          className="max-w-[180px] w-full h-auto object-contain"
          style={{ filter: 'drop-shadow(0 2px 0 rgba(0,0,0,0.6))' }}
        />
      </div>

      {/* Main Title */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3">
          <h1 className="text-4xl font-light text-white">
            Welcome back, {displayName?.split(' ')[0] || userName?.split(' ')[0] || 'there'}
          </h1>
        </div>
      </div>

      {/* Input Area */}
      <div className="w-full mb-4">
        {/* Additional Fields */}
        {selectedCategory && (
          <div className="mb-4">
            {renderFormFields()}
          </div>
        )}

        <div className="relative">
          <div 
            className={`
              border border-border-secondary rounded-xl bg-bg-secondary 
              focus-within:border-border-focus transition-all
              ${dragOver ? 'border-brand-yellow bg-bg-drag' : ''}
            `}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          >
            {/* Input Text Area */}
            <textarea
              value={localInputText}
              onChange={(e) => {
                if (selectedCategory === 'audio_transcription' || selectedCategory === 'video_transcription') return;
                setLocalInputText(e.target.value);
              }}
              placeholder={getPlaceholderText()}
              disabled={loading || selectedCategory === 'audio_transcription' || selectedCategory === 'video_transcription'}
              readOnly={selectedCategory === 'audio_transcription' || selectedCategory === 'video_transcription'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (loading) return;
                  const hasFiles = localSelectedFiles.length > 0;
                  if (!hasFiles) return;

                  try {
                    onSubmit({
                      selectedCategory,
                      inputText: localInputText,
                      selectedFiles: localSelectedFiles,
                      formData: localFormData,
                    });
                    setLocalInputText('');
                  } catch (err) {
                    console.warn('Submit failed:', err);
                  }
                }
              }}
              className="w-full min-h-[100px] px-4 pt-4 pb-2 bg-transparent border-none outline-none resize-none text-text-primary placeholder-text-muted text-[15px] leading-relaxed"
            />

            {/* File Upload Input */}
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              accept={getCategoryConfig()?.file_types?.join(',')}
              className="absolute left-0 top-0 w-0 h-0 opacity-0 pointer-events-none"
              id="file-input"
              disabled={loading}
            />

            {/* Files Display */}
            {localSelectedFiles.length > 0 && (
              <div className="px-4 pb-3 border-t border-bg-tertiary pt-3">
                <div className="flex flex-wrap gap-2">
                  {localSelectedFiles.map((file: File, index: number) => (
                    <div key={index} className="relative group">
                      {file.type.startsWith('image/') ? (
                        <div className="relative w-20 h-20 rounded-lg overflow-visible border border-border-secondary group">
                          <img
                            src={fileObjectUrls[index] || undefined}
                            alt={file.name}
                            onClick={() => openImageModal(index)}
                            role="button"
                            className="w-full h-full object-cover rounded-lg transition-transform duration-200 group-hover:scale-105 cursor-pointer"
                          />
                          <button
                            onClick={() => setLocalSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                            aria-label={`Remove ${file.name}`}
                            className="absolute -top-1 -left-1 w-5 h-5 bg-bg-tertiary text-text-tertiary rounded-full flex items-center justify-center transform opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-150 ring-1 ring-border-secondary pointer-events-none group-hover:pointer-events-auto z-40"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : file.type === 'application/pdf' ? (
                        <div className="relative w-20 h-20 rounded-lg overflow-visible border border-border-secondary group">
                          <object
                            data={fileObjectUrls[index] || undefined}
                            type="application/pdf"
                            aria-label={file.name}
                            className="w-full h-full rounded-lg overflow-hidden"
                          >
                            <div className="w-full h-full flex items-center justify-center text-text-secondary">PDF</div>
                          </object>
                          <button
                            onClick={() => setLocalSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                            aria-label={`Remove ${file.name}`}
                            className="absolute -top-1 -left-1 w-5 h-5 bg-bg-tertiary text-text-tertiary rounded-full flex items-center justify-center transform opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-150 ring-1 ring-border-secondary pointer-events-none group-hover:pointer-events-auto z-40"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary rounded-lg border border-border-secondary">
                          <svg className="w-4 h-4 text-text-tertiary" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-text-secondary text-xs truncate max-w-[100px]">{file.name}</span>
                          <button
                            onClick={() => setLocalSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                            className="ml-auto text-text-muted hover:text-text-primary"
                            aria-label={`Remove ${file.name}`}
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Image modal/lightbox */}
            {modalIndex !== null && modalSrc && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div
                  className="absolute inset-0 bg-black/40"
                  aria-hidden
                />
                <div ref={modalRef} className="relative max-w-[80%] max-h-[80%]">
                  <img src={modalSrc} alt={localSelectedFiles[modalIndex].name} className="w-full h-auto rounded-lg shadow-2xl" />
                  <button
                    onClick={closeImageModal}
                    className="absolute -top-4 -right-4 w-8 h-8 bg-bg-primary text-text-primary rounded-full flex items-center justify-center shadow-lg"
                    aria-label="Close image"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Bottom Controls */}
            {localDropError && (
              <div className="px-4 pt-2">
                <p className="text-xs text-semantic-error-light">{localDropError}</p>
              </div>
            )}
            {loadingMessage && (
              <div className="px-4 pt-2">
                <p className="text-xs text-text-muted">{loadingMessage}</p>
              </div>
            )}
            <div className="flex items-center justify-between px-4 pb-3 pt-1">
              <div ref={uploadMenuRef} className="flex items-center gap-2 relative upload-menu-container">
                <button
                  type="button"
                  onClick={() => setShowUploadMenu(!showUploadMenu)}
                  className="p-1.5 hover:bg-bg-tertiary rounded-lg transition-all"
                  title="Add attachments"
                >
                  <svg 
                    className={`w-5 h-5 text-text-tertiary transition-transform duration-200 ${showUploadMenu ? 'rotate-45' : 'rotate-0'}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>

                {showUploadMenu && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-bg-primary border border-border-secondary rounded-md shadow-2xl py-1 z-[9999]">
                    <label
                      htmlFor="file-input"
                      className="flex items-center gap-3 px-3 py-2 hover:bg-bg-secondary transition-colors cursor-pointer"
                    >
                      <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="text-sm text-text-secondary">Upload a file</span>
                    </label>
                  </div>
                )}
              </div>

              <button
                onClick={() => onSubmit({ selectedCategory, inputText: localInputText, selectedFiles: localSelectedFiles, formData: localFormData })}
                disabled={loading || localSelectedFiles.length === 0 || !selectedCategory}
                className="p-2 bg-brand-yellow text-black rounded-lg hover:bg-brand-yellow-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4" viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden stroke="currentColor" strokeWidth="1">
                  <path d="M27.66,15.61,18,6,8.34,15.61A1,1,0,1,0,9.75,17L17,9.81V28.94a1,1,0,1,0,2,0V9.81L26.25,17a1,1,0,0,0,1.41-1.42Z" />
                </svg>
              </button>
            </div>
          </div>

          {/* File Type Info Below Input */}
          <div className="mt-3 text-center">
            <p className="text-xs text-text-muted">
              {getCategoryConfig() ? `${getCategoryConfig()?.file_types?.map(t => t.includes('/') ? t.split('/').pop() : t).join(', ')} • Max ${getCategoryConfig()?.max_file_size}` : 'Select a category to see supported file types'}
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="w-full mb-4 p-3 bg-semantic-error-dark/20 border border-semantic-error rounded-xl text-semantic-error-light text-sm">
          {error}
        </div>
      )}

      {/* Category Buttons */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {Object.entries(availableCategories).map(([key, _category]) => (
          <button
            key={key}
            onClick={() => onSelectCategory(key)}
            className={`
              flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-all duration-200
              ${selectedCategory === key
                ? 'bg-white text-black border-white shadow-lg' 
                : 'bg-bg-secondary text-text-secondary border-border-secondary hover:border-border-focus hover:bg-bg-hover'
              }
            `}
          >
            <span className="flex-shrink-0">{getCategoryIcon(key)}</span>
            <span className="text-sm font-medium">{getCategoryButtonText(key)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default UploadArea;
